import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Save, ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import useNotification from "../common/Notification";
import Editor from "./Editor";
import DOMPurify from "dompurify";
import EmailEditorTest from "./EmailEditorTest";
import isValidate from "../../utils/validation";
import base_url from "../../constants";


const EmailTemplateEditor = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotification();


  // Check if template data was passed via navigation
  const initialTemplate = location.state?.templateData || {
    template_title: "",
    template: "",
    template_type: 0,
  };

  const isViewOnly = location.pathname.includes("/view/");
  const isEditing = location.pathname.includes("/edit/");

  const [enableHTML, setEnableHTML] = useState(false); // HTML toggle
  const [template, setTemplate] = useState(initialTemplate);
  const [editorMode, setEditorMode] = useState("raw");
  const editorRef = useRef(null);
  
  
  const userId = Cookies.get("userId");
  const userRole = Cookies.get("userRole")?.split(",")[0];

  useEffect(() => {
    if (id) fetchTemplate();
  }, [id]);

  const fetchTemplate = async () => {
    try {
      const res = await axios.get( base_url +
        "/email-template/getbyroleanduserid",
        {
          params: { user_id: userId, role: userRole },
        }
      );
      const found = res.data.data.find((t) => t.id === id);
      if (found) setTemplate(found);
    } catch (err) {
      console.error("Error fetching template:", err);
      notify("Failed to fetch template details", "error");
    }
  };

const handleSave = async () => {
  try {
    let htmlContent = template.template;
    let type = '0';
    let template_design = null;

    // 🔹 For Advanced Editor
    if (editorMode === "advanced" && editorRef.current) {
      await new Promise((resolve) => {
        editorRef.current.exportHtml((html, design) => {
          // content = html.trim();

          console.log(html);
          console.log(design);
          template_design = JSON.stringify(design);
          type = '1';
          htmlContent = html.trim();
          console.log("Exported design" , template_design);
          resolve();
        });
      });
    }

    // 🔹 Validation
    if (
      !isValidate("subject", template.template_title.trim()) ||
      !isValidate("editorContent", htmlContent.trim())
    ) {
      notify("Some fields are missing.", "error");
      return;
    }

    // 🔹 Create payload directly with `content` (not state)
    const payload = {
      user_id: userId,
      role: userRole,
      title: template.template_title,
      type : type,
      template: htmlContent,
      template_design: template_design,
    };

    if (isEditing) {
      await axios.post(base_url + "/email-template/update", {
        template_id: id,
        ...payload,
      });
      notify("Template updated successfully", "success");
    } else {
      await axios.post(base_url + "/email-template/create", payload);
      notify("Template created successfully", "success");
    }

    navigate("/templates");
  } catch (error) {
    console.error("Error saving template:", error);
    notify("Failed to save template", "error");
  }
};


  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setEditorMode(newMode);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        {/* <IconButton onClick={() => navigate("/templates")}>
          <ArrowBack />
        </IconButton> */}
              <Typography variant='h6'  sx={{ mb: 2, mt: 3, color : 'primary.main', width: 'fit-content', py:1, borderRadius:2, fontWeight:'bold'}}>
          {isEditing
            ? "Edit Email Template"
            : isViewOnly
            ? "View Template"
            : "Create Email Template"}
        </Typography>
      </Box>

      {/* Template Title */}
      <TextField
        fullWidth
        label="Template Title"
        name="template_title"
        value={template.template_title}
        onChange={(e) =>
          setTemplate((prev) => ({ ...prev, template_title: e.target.value }))
        }
        sx={{ mb: 3 }}
        disabled={isViewOnly}
      />

      {/* Toggle HTML Content */}
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Editor Mode
      </Typography>

      <ToggleButtonGroup
        value={editorMode}
        exclusive
        onChange={handleModeChange}
        color="primary"
        
      >
        <ToggleButton value="raw">Raw HTML</ToggleButton>
        <ToggleButton value="simple">Simple Editor</ToggleButton>
        {/* {isEditing &&   */}
        
       { (template.type == "1" || !isEditing) &&  <ToggleButton value="advanced">Advanced Editor</ToggleButton> }

        
        {/* } */}
      
      </ToggleButtonGroup>
      <Divider sx={{ my: 3 }} />
    

      {/* Editor or HTML Textarea */}
      {editorMode === "raw" &&  (
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Left: Textarea for HTML */}
          <textarea
            value={template.template}
            onChange={(e) =>
              setTemplate((prev) => ({ ...prev, template: e.target.value }))
            }
            style={{
              width: "50%",
              height: "25rem",
              padding: "1rem",
              fontFamily: "monospace",
              fontSize: "0.9rem",
              border: "1px solid #ccc",
              borderRadius: 4,
            }}
            placeholder="Paste your HTML content here"
          />

          {/* Right: Live Preview */}
          <Box
            sx={{
              width: "50%",
              height: "25rem",
              border: "1px solid #ccc",
              borderRadius: 2,
              p: 2,
              overflow: "auto",
              backgroundColor: "#fafafa",
            }}
            dangerouslySetInnerHTML={{
              // __html: DOMPurify.sanitize(template.template),
              __html:template.template,

            }}
          />
        </Box>
      ) }
      {editorMode === "simple" && 
       (
        // Normal React Quill editor
        <Editor
          editorContent={template.template}
          onChange={(content) =>
            setTemplate((prev) => ({ ...prev, template: content }))
          }
        />
      )}

        {editorMode === "advanced"  && isEditing && template.type == "1" &&  (
        // Email Template Editor component
        <EmailEditorTest ref={editorRef} editorContent={template.template_design} type={template.type} />
      )}

      {editorMode === "advanced"  && !isEditing  &&(
        // Email Template Editor component
        <EmailEditorTest ref={editorRef} editorContent={template.template_design} type={template.type} />
      )}

      {/* Save / Cancel */}
      {!isViewOnly && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/templates")}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={!template.template_title }
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default EmailTemplateEditor;
